import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  TimelineItem,
  TimelineItemContent,
  TimelineItemTrigger,
} from '@rtkwlf/c3-recipes-timeline';
import { faAsterisk } from '@rtkwlf/pro-solid-svg-icons/faAsterisk';
import { faExclamationTriangle } from '@rtkwlf/pro-solid-svg-icons/faExclamationTriangle';
import { SpidrState, type Spidr } from '@rtkwlf/track-public';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useGetOrganizationSpidrSummaryOptions } from '@apis/track-public/query-options';

import { routes } from '../../../../../constants/routes';
import { useSecurityJourneyTranslations } from '../../../../../locales/use-security-journey-translations';

import { SecurityFocusTimelineItemEmpty } from './security-focus-timeline-item-empty';
import { SecurityFocusTimelineItemError } from './security-focus-timeline-item-error';
import { SecurityFocusTimelineItemLoading } from './security-focus-timeline-item-loading';
import { SecurityFocusTimelineSpidrContent } from './security-focus-timeline-spidr-content';
import styles from './security-focus-timeline-spidr.module.css';

/* -------------------------------------------------------------------------------------------------
 * Constants and Utilities
 * -----------------------------------------------------------------------------------------------*/
type SpidrRequestArgs = {
  spidrID: Spidr['id'];
};

/* -------------------------------------------------------------------------------------------------
 * SecurityFocusTimelineAdhocContent
 * -----------------------------------------------------------------------------------------------*/
type SecurityFocusTimelineAdhocContentProps = SpidrRequestArgs;

const SecurityFocusTimelineAdhocContent: React.FunctionComponent<
  SecurityFocusTimelineAdhocContentProps
> = ({ spidrID }) => {
  /**
   * Fetch details for the spidr
   */
  const {
    data: summary,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    useGetOrganizationSpidrSummaryOptions({
      spidrID,
    }),
  );

  /**
   * Get an instance of translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Return the loading state for the spidr
   */
  if (isLoading) {
    return <SecurityFocusTimelineItemLoading />;
  }

  /**
   * Return the error state for the spidr
   */
  if (isError) {
    return (
      <SecurityFocusTimelineItemError
        icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
        description={t('focusID.timeline.adhoc.error')}
        onRetry={refetch}
      />
    );
  }

  /**
   * Return an empty state if the spidr is undefined
   */
  if (!summary) {
    return (
      <SecurityFocusTimelineItemEmpty
        description={t('focusID.timeline.adhoc.unknown_description')}
        icon={<FontAwesomeIcon icon={faAsterisk} />}
        title={t('focusID.timeline.adhoc.unknown')}
      />
    );
  }

  return (
    <SecurityFocusTimelineSpidrContent
      adhoc
      expiryDate={summary.expiryDate}
      name={summary.name}
      state={summary.state}
      ticketId={summary.ticketID}
      tier={summary.tier}
    />
  );
};

/* -------------------------------------------------------------------------------------------------
 * SecurityFocusTimelineAdhoc
 * -----------------------------------------------------------------------------------------------*/
type SecurityFocusTimelineAdhocProps = SpidrRequestArgs;

const SecurityFocusTimelineAdhoc = ({
  spidrID,
}: SecurityFocusTimelineAdhocProps) => {
  /**
   * Get an instance of translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Get an instance of the query client
   */
  const queryClient = useQueryClient();

  /**
   * Get spidr query options
   */
  const spidrSummaryQueryOptions = useGetOrganizationSpidrSummaryOptions({
    spidrID,
  });

  /**
   * Prefetch the adhoc spidr details when hovering the timeline item
   */
  const onMouseEnterTrigger = () => {
    void queryClient.prefetchQuery({
      ...spidrSummaryQueryOptions,
      staleTime: 5000,
    });
  };

  /**
   * Fetch details for the spidr
   */
  const { data: summary } = useQuery(spidrSummaryQueryOptions);

  const isAdhocSpidrDeliveredOrComplete =
    summary &&
    (summary.state === SpidrState.Delivered ||
      summary.state === SpidrState.Complete);

  return (
    <TimelineItem>
      <TimelineItemTrigger
        asChild={isAdhocSpidrDeliveredOrComplete}
        attributes={{ onMouseEnter: onMouseEnterTrigger }}
        label={t('focusID.timeline.spidrs.adhoc')}
        color="grey"
        size="sm"
        variant="secondary"
      >
        {isAdhocSpidrDeliveredOrComplete ? (
          <Link
            to={routes.spidrs.spidrID.index({
              spidrID,
            })}
          >
            <FontAwesomeIcon icon={faAsterisk} />
          </Link>
        ) : (
          <FontAwesomeIcon icon={faAsterisk} />
        )}
      </TimelineItemTrigger>
      <TimelineItemContent
        className={styles['sft-spidr-item-content']}
        data-testid="security-focus-timeline-item-adhoc"
      >
        <SecurityFocusTimelineAdhocContent spidrID={spidrID} />
      </TimelineItemContent>
    </TimelineItem>
  );
};

export { SecurityFocusTimelineAdhoc };
export type { SecurityFocusTimelineAdhocProps };
